import React, { useEffect, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

// Shared identity block. Both legal pages are standalone documents that may be
// linked directly (Google Play, the app, an email), so each one has to name the
// operator and contact details on its own rather than relying on the other.
export const OPERATOR = 'Simco & Sons Ltd';
export const RC_NUMBER = 'RC No. 9353317';
export const CONTACT_EMAIL = 'hello@ogastock.com';
export const CONTACT_PHONE = '+2349062016800';
export const CONTACT_ADDRESS = 'Plot 146 Community Estate, Trans Ekulu, Enugu';
export const EFFECTIVE_DATE = '20 August 2026';

type Props = {
  title: string;
  /** Short line under the title saying what the document is. */
  intro: string;
  /** The other legal page, cross-linked at the foot of this one. */
  otherHref: string;
  otherLabel: string;
  children: React.ReactNode;
};

export default function LegalPage({ title, intro, otherHref, otherLabel, children }: Props) {
  // React Router keeps scroll position across routes, so arriving here from the
  // footer link — which sits at the very bottom of the home page — otherwise
  // lands you at the bottom of the legal page, which reads as a blank screen.
  //
  // The global `html{scroll-behavior:smooth}` makes a plain scrollTo(0, 0)
  // asynchronous — it starts an animation that the route change then cancels,
  // leaving the page still at the bottom (observed on the real page, not
  // theoretical). An explicit `behavior: 'instant'` scrolls synchronously and
  // is the only form that actually sticks here.
  //
  // The cast is because TypeScript 4.9's DOM lib predates 'instant' and still
  // types ScrollBehavior as 'auto' | 'smooth'; the value is valid at runtime.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as unknown as ScrollBehavior });
  }, []);

  useEffect(() => {
    document.title = title + ' · OgaStock';
  }, [title]);

  return (
    <>
      <Header />
      <main className="legal">
        <div className="wrap legal-wrap">
          <p className="eyebrow">Legal</p>
          <h1>{title}</h1>
          <p className="legal-intro">{intro}</p>

          <div className="legal-meta">
            <p>
              <strong>Effective date:</strong> {EFFECTIVE_DATE}
            </p>
            <p>
              <strong>Operator:</strong> {OPERATOR}, a business registered in Nigeria ({RC_NUMBER}) (“One13”).
            </p>
            <p>
              <strong>Contact:</strong>{' '}
              <a href={'mailto:' + CONTACT_EMAIL}>{CONTACT_EMAIL}</a> · {CONTACT_PHONE} · {CONTACT_ADDRESS}
            </p>
          </div>

          {children}

          <p className="legal-crosslink">
            See also: <Link to={otherHref}>{otherLabel}</Link>
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
}
